<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('HOLIDAYS')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.countryCode"
                    :items="countriesFilterItems"
                    :loading="countriesFilter.isLoading"
                    :search-input.sync="countriesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('holidays.country')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="countriesFilter.isEdit = true"
                    @change="countriesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    :items="agenciesFilterItems"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('holidays.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.postalCodeCode"
                    :items="postalCodesFilterItems"
                    :loading="postalCodesFilter.isLoading"
                    :search-input.sync="postalCodesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('holidays.postalCode')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="postalCodesFilter.isEdit = true"
                    @change="postalCodesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="3" cols="12">
                  <v-dialog
                    v-model="modalFilterDateFrom"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateFrom = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.dateFromFormatted"
                        :label="$t('holidays.dateFrom')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateFrom = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateFrom"
                      scrollable
                      @input="modalFilterDateFrom = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateFrom = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="12" md="3" cols="12">
                  <v-dialog
                    v-model="modalFilterDateTo"
                    :close-on-content-click="false"
                    persistent
                    width="290px"
                    @keydown.esc="modalFilterDateTo = false"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="filter.dateToFormatted"
                        :label="$t('holidays.dateTo')"
                        append-icon="far fa-calendar-alt"
                        readonly
                        clearable
                        v-on="on"
                        @click:clear="filter.dateTo = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.dateTo"
                      scrollable
                      @input="modalFilterDateTo = false"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="modalFilterDateTo = false"
                      >
                        {{ $t("global.cancel") }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col sm="12" md="2" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/holidays"
              :filter="this.filter"
              :filterExport="filterExport"
            />
            <ImportExcel
              url="/holidays"
              @done="search"
              :filterImport="filterImport"
              v-if="$checkPermission('HOLIDAYS')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('HOLIDAYS')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('HOLIDAYS')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('HOLIDAYS')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code)"
                  ></crud-button>
                </td>
                <td>{{ transformDate(item.date, $t("global.dateFormat")) }}</td>
                <td>{{ item.description }}</td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.country.code }}</span>
                    </template>
                    <span>{{ item.country.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip
                    right
                    v-if="item.agency !== undefined && item.agency !== null"
                  >
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agency.code }}</span>
                    </template>
                    <span>{{ item.agency.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <template
                    v-if="
                      item.postalCode !== undefined && item.postalCode !== null
                    "
                  >
                    {{ item.postalCode.code }}
                  </template>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-dialog
                  v-model="modalDateHoliday"
                  :close-on-content-click="false"
                  persistent
                  width="290px"
                  @keydown.esc="modalDateHoliday = false"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="item.dateFormatted"
                      :label="$t('holidays.date')"
                      append-icon="far fa-calendar-alt"
                      readonly
                      clearable
                      v-on="on"
                      @click:clear="item.date = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="item.date"
                    scrollable
                    @input="modalDateHoliday = false"
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="modalDateHoliday = false"
                    >
                      {{ $t("global.cancel") }}
                    </v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('holidays.description')"
                    rules="required|max:60"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('holidays.description')"
                      v-model="item.description"
                      required
                      :error-messages="errors"
                      counter="60"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('holidays.country')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.countryCode"
                      :items="countriesItems"
                      :loading="countries.isLoading"
                      :search-input.sync="countries.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('holidays.country')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="countries.isEdit = true"
                      @change="
                        countries.isEdit = false;
                        resetAgenciesSearch();
                        resetPostalCodesSearch();
                      "
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <v-autocomplete
                    v-model="item.agencyCode"
                    :items="agenciesItems"
                    :loading="agencies.isLoading"
                    :search-input.sync="agencies.search"
                    hide-no-data
                    :disabled="isCountrySelected"
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('holidays.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agencies.isEdit = true"
                    @change="
                      agencies.isEdit = false;
                      resetPostalCodesSearch();
                    "
                    clearable
                  ></v-autocomplete>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <v-autocomplete
                    v-model="item.postalCodeCode"
                    :items="postalCodesItems"
                    :loading="postalCodes.isLoading"
                    :search-input.sync="postalCodes.search"
                    hide-no-data
                    :disabled="isCountrySelected"
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('holidays.postalCode')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="postalCodes.isEdit = true"
                    @change="postalCodes.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import { transformDate, dateToIso } from "@/js/utils.js";

const CONTEXT = "/holidays";
const CONTEXT_COUNTRIES = "/countries";
const CONTEXT_AGENCIES = "/agencies";
const CONTEXT_POSTAL_CODES = "/postalCodes";
const ENTITY_NAME = "holiday.";

export default {
  components: {
    CrudButton,
    ExportExcel,
    ImportExcel
  },
  name: "PostalCode",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    modalDateHoliday: false,
    modalFilterDateFrom: false,
    modalFilterDateTo: false,
    editMode: false,
    dialog: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      dateFrom: null,
      dateFromFormatted: "",
      dateTo: null,
      dateToFormatted: "",
      countryCode: "",
      agencyCode: "",
      postalCodeCode: "",
      deletedItems: "ALL"
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    countries: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    countriesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    postalCodes: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    postalCodesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("holidays.title.edit")
        : this.$t("holidays.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("holidays.datatable.date"),
          value: ENTITY_NAME + "date"
        },
        {
          text: this.$t("holidays.datatable.description"),
          value: ENTITY_NAME + "description"
        },
        {
          text: this.$t("holidays.datatable.country"),
          value: ENTITY_NAME + "countryCode"
        },
        {
          text: this.$t("holidays.datatable.agency"),
          value: ENTITY_NAME + "agencyCode"
        },
        {
          text: this.$t("holidays.datatable.postalCode"),
          value: ENTITY_NAME + "postalCodeCode"
        }
      ];
    },
    countriesItems() {
      return this.countries.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    countriesFilterItems() {
      return this.countriesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesFilterItems() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    postalCodesItems() {
      return this.postalCodes.entries.map(entry => {
        const description = entry.code;
        return Object.assign({}, entry, { description });
      });
    },
    postalCodesFilterItems() {
      return this.postalCodesFilter.entries.map(entry => {
        const description = entry.code;
        return Object.assign({}, entry, { description });
      });
    },
    isCountrySelected() {
      return (
        this.item.countryCode === undefined || this.item.countryCode === null
      );
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "countries.search": function(value) {
      this.preLoadGenericSearchEntity(value, this.countries, CONTEXT_COUNTRIES);
    },
    "countriesFilter.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.countriesFilter,
        CONTEXT_COUNTRIES
      );
    },
    "agencies.search": function(value) {
      let aditionalFilter = {
        countryCode: this.item.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agencies,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    },
    "agenciesFilter.search": function(value) {
      let aditionalFilter = {
        countryCode: this.filter.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    },
    "postalCodes.search": function(value) {
      let aditionalFilter = {
        agencyCode: this.item.agencyCode,
        countryCode: this.item.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.postalCodes,
        CONTEXT_POSTAL_CODES,
        aditionalFilter
      );
    },
    "postalCodesFilter.search": function(value) {
      let aditionalFilter = {
        agencyCode: this.filter.agencyCode,
        countryCode: this.filter.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.postalCodesFilter,
        CONTEXT_POSTAL_CODES,
        aditionalFilter
      );
    },
    "filter.dateFrom": function() {
      this.filter.dateFromFormatted = this.transformDate(
        this.filter.dateFrom,
        this.$t("global.dateFormat")
      );
    },
    "filter.dateTo": function() {
      this.filter.dateToFormatted = this.transformDate(
        this.filter.dateTo,
        this.$t("global.dateFormat")
      );
    },
    "item.date": function() {
      this.item.dateFormatted = this.transformDate(
        this.item.date,
        this.$t("global.dateFormat")
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      // Items have already been requested
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");
      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      setTimeout(() => {
        this.countries.search = item.country.code + " - " + item.country.name;
        if (item.agency !== undefined && item.agency !== null) {
          this.agencies.search = item.agency.code + " - " + item.agency.name;
        }
        if (item.postalCode !== undefined && item.postalCode !== null) {
          this.postalCodes.search = item.postalCode.code;
        }
      }, 500);
    },
    async update() {
      console.debug("Update form");
      localhttp
        .put(CONTEXT + "/update/" + this.item.code, this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetCountriesSearch();
      this.resetAgenciesSearch();
      this.resetPostalCodesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetCountriesSearch() {
      this.countries.search = "";
      this.countries.entries = [];
    },
    resetAgenciesSearch() {
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    resetPostalCodesSearch() {
      this.postalCodes.search = "";
      this.postalCodes.entries = [];
    },
    transformDate,
    filterExport(jsonData) {
      var vm = this;
      jsonData.forEach(function(v) {
        delete v.country;
        delete v.agency;
        delete v.postalCode;
        v.date = transformDate(v.date, vm.$t("global.dateFormat"));
      });
    },
    filterImport(jsonData) {
      var vm = this;
      jsonData.forEach(function(v) {
        v.date = dateToIso(v.date, vm.$t("global.dateFormat"));
      });
    }
  },
  mounted() {
    this.filter.dateFrom = new Date().toISOString().substr(0, 10);
  }
};
</script>
